import {getComboMarkdownEditor} from './comp/ComboMarkdownEditor.ts';
import {POST} from '../modules/fetch.ts';
import {showErrorToast} from '../modules/toast.ts';

// uploadSessionFiles posts each file as its own multipart request so one
// rejected file (wrong type, over the cap) never fails the others, collects the
// server's reasons for the ones refused, and signals the message list once.
// Exported for unit tests.
export async function uploadSessionFiles(
  filesUrl: string,
  scope: string,
  files: File[],
  attachButton: HTMLButtonElement | null,
  errorEl: HTMLElement | null,
): Promise<void> {
  if (!files.length) return;
  if (attachButton) attachButton.disabled = true;
  if (errorEl) errorEl.textContent = '';
  const failures: string[] = [];
  try {
    for (const file of files) {
      const formData = new FormData();
      formData.append('file', file, file.name);
      try {
        const res = await POST(`${filesUrl}?scope=${encodeURIComponent(scope)}`, {data: formData});
        if (!res.ok) {
          const reason = await res.text();
          failures.push(`${file.name}: ${reason.trim() || res.statusText}`);
        }
      } catch (err) {
        console.error('Failed to upload DRCY session file', err);
        failures.push(`${file.name}: upload failed`);
      }
    }
  } finally {
    if (attachButton) attachButton.disabled = false;
  }
  if (errorEl) errorEl.textContent = failures.join(' ');
  if (failures.length < files.length) {
    document.dispatchEvent(new CustomEvent('drcy-chat-files-changed'));
  }
}

// Wires the DRCY chat compose box (the real Gitea comment editor) and the End
// Chat button. It coordinates with the message list (the <drcy-chat> Vue
// component) only through the server: sending posts a message, the server
// dispatches the turn and publishes SSE events that the message list renders;
// ending cancels the run, clears messages, and publishes a "cleared" event.
export function initDrcyChat(): void {
  const resetButton = document.querySelector<HTMLButtonElement>('.drcy-chat-entry-reset');
  resetButton?.addEventListener('click', async () => {
    const resetUrl = resetButton.getAttribute('data-reset-url');
    if (!resetUrl || resetButton.disabled) return;
    const scope = resetButton.getAttribute('data-scope') ?? '';
    const errorEl = document.querySelector<HTMLElement>('.drcy-chat-entry-error');
    resetButton.disabled = true;
    errorEl?.classList.add('tw:hidden');
    try {
      const url = new URL(resetUrl, window.location.href);
      url.searchParams.set('scope', scope);
      const response = await POST(url.toString());
      if (!response.ok) throw new Error(`Chat reset failed (${response.status})`);
      window.location.reload();
    } catch (err) {
      console.error('Failed to reset DRCY chat on context entry', err);
      errorEl?.classList.remove('tw:hidden');
      resetButton.disabled = false;
    }
  });

  let hasArtifactDrafts = false;
  document.addEventListener('drcy-chat-artifacts-dirty', (e: Event) => {
    hasArtifactDrafts = (e as CustomEvent<boolean>).detail;
  });
  const composeArea = document.querySelector<HTMLElement>('.drcy-chat-compose-area');
  if (composeArea) {
    const editorEl = composeArea.querySelector<HTMLElement>('.combo-markdown-editor');
    const sendButton = composeArea.querySelector<HTMLButtonElement>('.drcy-chat-send');
    const postUrl = composeArea.getAttribute('data-post-url');
    const scope = composeArea.getAttribute('data-scope') ?? '';
    const filesUrl = composeArea.getAttribute('data-files-url');
    const contextCommentId = Number(composeArea.getAttribute('data-context-comment-id')) || undefined;
    const contextIssueIndex = Number(composeArea.getAttribute('data-context-issue-index')) || undefined;
    const contextAllReviews = composeArea.getAttribute('data-context-all-reviews') === 'true';

    const submit = async () => {
      const editor = getComboMarkdownEditor(editorEl);
      if (!editor || !postUrl || sendButton?.disabled) return;
      const content = editor.value().trim();
      if (!content) return;
      sendButton.disabled = true;
      try {
        const res = await POST(postUrl, {data: {content, scope, context_comment_id: contextCommentId, context_issue_index: contextIssueIndex, context_all_reviews: contextAllReviews}});
        // On success clear the box and keep the button disabled: the turn is now
        // in flight, and the message list re-enables it via 'drcy-chat-busy' when
        // the turn finishes. On 409 (a turn is still running) or any error, leave
        // the text and re-enable so the user can retry.
        if (res.ok) {
          editor.value('');
          const result = await res.json();
          if (result.transcript_reset) {
            document.dispatchEvent(new CustomEvent('drcy-chat-transcript-reset'));
          }
          // Tell the message list to reconcile from history immediately rather
          // than waiting for the SSE 'message' events. If the stream is silently
          // dead or served by another Hub process, this is what makes the new
          // turn appear and starts the fallback poll (so Send re-enables).
          document.dispatchEvent(new CustomEvent('drcy-chat-reconcile', {
            detail: {turnId: result.turn_id, dispatched: result.dispatched, queuedBehind: result.queued_behind},
          }));
        } else {
          showErrorToast(`DRCY could not send this message (${res.status}).`);
          sendButton.disabled = false;
        }
      } catch (err) {
        console.error('Failed to send DRCY chat message', err);
        showErrorToast('DRCY could not send this message. Check your connection and try again.');
        sendButton.disabled = false;
      }
    };

    sendButton?.addEventListener('click', submit);

    // The message list (<drcy-chat>) drives the composer's enabled state: send is
    // replaced by Stop while a turn is responding and restored once nothing is in
    // flight. Disabling Send also guards keyboard submission and is authoritative
    // over the optimistic disable in submit(), so a
    // reload mid-turn (or a turn started elsewhere) also disables send.
    document.addEventListener('drcy-chat-busy', (e: Event) => {
      if (sendButton) {
        const busy = (e as CustomEvent<boolean>).detail;
        sendButton.disabled = busy;
        sendButton.classList.toggle('tw:hidden', busy);
      }
    });
    // Ctrl/Cmd+Enter sends, matching Gitea's comment-form shortcut.
    editorEl?.addEventListener('keydown', (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
        e.preventDefault();
        submit();
      }
    });

    // Session files: the attach button and drag-and-drop on the compose area
    // upload each file in its own request, then tell the message list to
    // refresh its chip row. The composer stays enabled throughout, so the user
    // types what DRCY should do with the upload and sends it as a normal
    // message. File contents are never read into the page.
    const attachButton = composeArea.querySelector<HTMLButtonElement>('.drcy-chat-attach');
    const fileInput = composeArea.querySelector<HTMLInputElement>('.drcy-chat-file-input');
    const uploadError = composeArea.querySelector<HTMLElement>('.drcy-chat-upload-error');
    if (filesUrl && attachButton && fileInput) {
      attachButton.addEventListener('click', () => fileInput.click());
      fileInput.addEventListener('change', async () => {
        const files = Array.from(fileInput.files ?? []);
        fileInput.value = '';
        await uploadSessionFiles(filesUrl, scope, files, attachButton, uploadError);
      });
      composeArea.addEventListener('dragover', (e: DragEvent) => {
        if (!e.dataTransfer?.types.includes('Files')) return;
        e.preventDefault();
        composeArea.classList.add('drcy-chat-dragover');
      });
      composeArea.addEventListener('dragleave', () => composeArea.classList.remove('drcy-chat-dragover'));
      composeArea.addEventListener('drop', async (e: DragEvent) => {
        composeArea.classList.remove('drcy-chat-dragover');
        const files = Array.from(e.dataTransfer?.files ?? []);
        if (!files.length) return;
        e.preventDefault();
        await uploadSessionFiles(filesUrl, scope, files, attachButton, uploadError);
      });
    }

    // The landing-state example prompts (in the <drcy-chat> component) prefill
    // the composer via `drcy-chat-prefill`, replacing what is there; a sheet
    // cell's "Ask DRCY" adds its question via `drcy-chat-append`, keeping what
    // the engineer already typed. Either way the composer takes focus with the
    // caret at the end so the user can review and send.
    const fillComposer = (text: string, append: boolean) => {
      const editor = getComboMarkdownEditor(editorEl);
      if (!editor) return;
      const current = append ? String(editor.value() ?? '').trimEnd() : '';
      editor.value(current ? `${current}\n\n${text}` : text);
      const textarea = editorEl?.querySelector('textarea');
      textarea?.focus();
      textarea?.setSelectionRange(textarea.value.length, textarea.value.length);
    };
    document.addEventListener('drcy-chat-prefill', (e: Event) => fillComposer((e as CustomEvent<string>).detail, false));
    document.addEventListener('drcy-chat-append', (e: Event) => fillComposer((e as CustomEvent<string>).detail, true));

    for (const starter of document.querySelectorAll<HTMLButtonElement>('.drcy-chat-context-starter')) {
      starter.addEventListener('click', () => {
        document.dispatchEvent(new CustomEvent('drcy-chat-prefill', {detail: starter.getAttribute('data-prompt') ?? ''}));
      });
    }
  }

  // The repo-level scope picker is a fomantic dropdown of anchor links
  // (?scope=…), so selecting an option navigates on its own — no JS needed.

  const endButton = document.querySelector<HTMLButtonElement>('.drcy-chat-end');
  endButton?.addEventListener('click', async () => {
    const endUrl = endButton.getAttribute('data-end-url');
    if (!endUrl) return;
    const scope = endButton.getAttribute('data-scope') ?? '';
    if (hasArtifactDrafts && !window.confirm('End this chat and discard all uncommitted artifacts?')) return;
    endButton.disabled = true;
    try {
      const res = await POST(`${endUrl}?scope=${encodeURIComponent(scope)}`);
      // Clear the transcript locally on success instead of relying solely on the
      // SSE 'cleared' event: if the stream is dead or on another Hub process the
      // server still deleted the conversation, and the view must reflect that.
      if (res.ok) {
        document.dispatchEvent(new CustomEvent('drcy-chat-ended'));
      }
    } catch (err) {
      console.error('Failed to end DRCY chat', err);
    } finally {
      endButton.disabled = false;
    }
  });
}
